import * as React from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { translate } from 'secullum-i18n';
import {
  Details,
  DetailsHeader,
  DetailsLine,
  Message,
  Space,
  Text,
  formatDate,
  isTablet
} from 'secullum-react-native-ui';
import {
  getCorStatus,
  getIconeStatus,
  getPeriodoFormatado
} from '../../shared/containers/AssinaturaDigitalCartaoPontoContainer';
import { getTheme } from '../../shared/modules/layout';
import {
  TipoAlteracaoAssinaturaDigitalCartaoPonto,
  AssinaturaDigitalCartaoPontoDados,
  AssinaturaDigitalCartaoPontoStatus
} from '../../shared/modules/types';
import { GeolocationCoordinates } from '../modules/geolocation';
import { primeiraLetraMaiscula } from '../modules/string';
import AssinaturaDigitalCartaoPontoProcessamento from './AssinaturaDigitalCartaoPontoProcessamento';
import { ButtonBar } from './ButtonBar';
import VisualizadorPdf from './VisualizadorPdf';

interface Props {
  assinaturaDigitalCartaoPonto: AssinaturaDigitalCartaoPontoDados;
  onHabilitarScroll?: (habilitar: boolean) => void;
  onErroConexao?: (mensagem: string, onTentarNovamente: () => void) => void;
  coordenadas: GeolocationCoordinates | null;
  gerente?: boolean;
}

interface State {
  assinaturaDigitalCartaoPontoAtual: AssinaturaDigitalCartaoPontoDados | null;
  tipoAcao: TipoAlteracaoAssinaturaDigitalCartaoPonto | null;
  erros: string | null;
}

const DATE_FORMAT = 'dd/MM/yyyy HH:mm:ss';

class AssinaturaDigitalCartaoPontoDetalhe extends React.Component<
  Props,
  State
> {
  state: State = {
    assinaturaDigitalCartaoPontoAtual: null,
    tipoAcao: null,
    erros: null
  };

  visualizadorPdf: VisualizadorPdf | null = null;

  async componentDidMount() {
    const { assinaturaDigitalCartaoPonto } = this.props;

    this.setState({
      assinaturaDigitalCartaoPontoAtual: assinaturaDigitalCartaoPonto
    });
  }

  finalizarProcessamento = () => {
    this.setState({ tipoAcao: null });
  };

  concluirProcessamentoComErro = (erros: string) => {
    this.setState({
      tipoAcao: null,
      erros
    });
  };

  renderBotoes = () => {
    const { coordenadas } = this.props;

    if (!coordenadas) {
      return (
        <View style={[styles.container, styles.containerProcessando]}>
          <ActivityIndicator color="#aaa" />
          <Text style={styles.text}>
            {translate('Carregando coordenadas') + '...'}
          </Text>
        </View>
      );
    }

    return (
      <View style={styles.container}>
        <ButtonBar
          leftButton={{
            text: translate('Aprovar'),
            onPress: () =>
              this.setState({
                tipoAcao: TipoAlteracaoAssinaturaDigitalCartaoPonto.Aprovar
              }),
            style: { backgroundColor: theme.successColor },
            nativeID: 'botao-aprovar-assinatura-digital-cartao-ponto-detalhes'
          }}
          rightButton={{
            text: translate('Reprovar'),
            onPress: () =>
              this.setState({
                tipoAcao: TipoAlteracaoAssinaturaDigitalCartaoPonto.Rejeitar
              }),
            style: { backgroundColor: theme.errorColor },
            nativeID: 'botao-reprovar-assinatura-digital-cartao-ponto-detalhes'
          }}
        />
      </View>
    );
  };

  renderMotivoRejeicao = (titulo: string, observacao: string) => {
    return (
      <View style={styles.container}>
        <Text style={styles.text}>{titulo}</Text>
        <Text style={styles.text}>{observacao}</Text>
      </View>
    );
  };

  getTextoStatus = (estado: AssinaturaDigitalCartaoPontoStatus) => {
    switch (estado) {
      case AssinaturaDigitalCartaoPontoStatus.Aprovado:
        return translate('Aceito');
      case AssinaturaDigitalCartaoPontoStatus.Rejeitado:
        return translate('Rejeitado');
      case AssinaturaDigitalCartaoPontoStatus.NaoRespondido:
        return translate('Aguardando Retorno');
      default:
        // Assinaturas que estão "AguardandoAprovacaoGerentes" ainda não estão disponíveis para o funcionário.
        // Nesse caso, como não está aguardando nada do funcionário, ainda não exibimos a linha de resposta dele.
        return '';
    }
  };

  montarLinhasInformacoes = () => {
    const { gerente } = this.props;

    const { assinaturaDigitalCartaoPontoAtual } = this.state;

    if (!assinaturaDigitalCartaoPontoAtual) {
      return [];
    }

    const linhasInformacoes: Array<DetailsLine> = [
      {
        title: translate('Número'),
        value:
          assinaturaDigitalCartaoPontoAtual.assinaturaDigitalCartaoPontoId.toString()
      },
      ...(gerente
        ? [
            {
              title: translate('Funcionário'),
              value: assinaturaDigitalCartaoPontoAtual.funcionarioNome
            }
          ]
        : []),
      {
        title: translate('Criado em'),
        value: formatDate(
          assinaturaDigitalCartaoPontoAtual.dataInclusao,
          DATE_FORMAT
        )
      }
    ];

    const statusGerente =
      assinaturaDigitalCartaoPontoAtual.estadoGerente ||
      AssinaturaDigitalCartaoPontoStatus.NaoRespondido;

    if (gerente) {
      linhasInformacoes.push({
        title: translate('Resposta do gerente'),
        value: this.getTextoStatus(statusGerente),
        icons: [
          {
            name: getIconeStatus(statusGerente),
            color: getCorStatus(statusGerente),
            size: 18
          }
        ]
      });

      if (assinaturaDigitalCartaoPontoAtual.dataRespostaGerente) {
        linhasInformacoes.push({
          title: translate('Respondido pelo gerente em'),
          value: formatDate(
            assinaturaDigitalCartaoPontoAtual.dataRespostaGerente,
            DATE_FORMAT
          )
        });
      }

      if (assinaturaDigitalCartaoPontoAtual.motivoGerente) {
        linhasInformacoes.push({
          render: () =>
            this.renderMotivoRejeicao(
              translate('Motivo do gerente'),
              assinaturaDigitalCartaoPontoAtual.motivoGerente
            )
        });
      }
    }

    const labels = {
      resposta: gerente
        ? primeiraLetraMaiscula(translate('Resposta do Funcionário'))
        : translate('Status'),
      data: gerente
        ? translate('Respondido pelo funcionário em')
        : translate('Respondido em'),
      motivo: gerente ? translate('Motivo do funcionário') : translate('Motivo')
    };

    const respostaFuncionario = this.getTextoStatus(
      assinaturaDigitalCartaoPontoAtual.estado
    );

    if (respostaFuncionario) {
      linhasInformacoes.push({
        title: labels.resposta,
        value: respostaFuncionario,
        icons: [
          {
            name: getIconeStatus(assinaturaDigitalCartaoPontoAtual.estado),
            color: getCorStatus(assinaturaDigitalCartaoPontoAtual.estado),
            size: 18
          }
        ]
      });
    }

    if (assinaturaDigitalCartaoPontoAtual.dataResposta) {
      linhasInformacoes.push({
        title: labels.data,
        value: formatDate(
          assinaturaDigitalCartaoPontoAtual.dataResposta,
          DATE_FORMAT
        )
      });
    }

    if (assinaturaDigitalCartaoPontoAtual.motivo) {
      linhasInformacoes.push({
        render: () =>
          this.renderMotivoRejeicao(
            labels.motivo,
            assinaturaDigitalCartaoPontoAtual.motivo
          )
      });
    }

    const estado = gerente
      ? assinaturaDigitalCartaoPontoAtual.estadoGerente
      : assinaturaDigitalCartaoPontoAtual.estado;

    if (estado === AssinaturaDigitalCartaoPontoStatus.NaoRespondido) {
      linhasInformacoes.push({
        render: () => this.renderBotoes()
      });
    }

    return linhasInformacoes;
  };

  render() {
    const { assinaturaDigitalCartaoPonto, gerente } = this.props;
    const { erros, assinaturaDigitalCartaoPontoAtual, tipoAcao } = this.state;
    const { coordenadas } = this.props;

    return (
      <>
        <DetailsHeader
          text={`${
            assinaturaDigitalCartaoPonto.descricao
          }\n${getPeriodoFormatado(
            assinaturaDigitalCartaoPonto,
            'dd/MM/yyyy'
          )}`}
        />
        <Space />
        <Details
          title={translate('Informações')}
          lines={this.montarLinhasInformacoes()}
        />
        <Space />
        {assinaturaDigitalCartaoPontoAtual && (
          <VisualizadorPdf
            ref={ref => {
              if (ref) {
                this.visualizadorPdf = ref;
              }
            }}
            assinaturaDigitalCartaoPonto={assinaturaDigitalCartaoPontoAtual!}
            estado={tipoAcao}
            gerente={gerente}
            onHabilitarScroll={this.props.onHabilitarScroll}
            onError={error => this.setState({ erros: error })}
          />
        )}
        {tipoAcao != null &&
          assinaturaDigitalCartaoPontoAtual &&
          coordenadas && (
            <AssinaturaDigitalCartaoPontoProcessamento
              assinaturaDigitalCartaoPontoAtual={
                assinaturaDigitalCartaoPontoAtual
              }
              tipoAcao={tipoAcao}
              coordenadas={coordenadas}
              concluirEnvioSucesso={assinaturaDigitalCartaoPonto => {
                this.setState({
                  tipoAcao: null,
                  assinaturaDigitalCartaoPontoAtual:
                    assinaturaDigitalCartaoPonto
                });

                if (this.visualizadorPdf) {
                  this.visualizadorPdf.carregarPdfAsync(true);
                }
              }}
              concluirEnvioErro={erros =>
                this.concluirProcessamentoComErro(erros)
              }
              finalizarProcessamento={this.finalizarProcessamento}
              gerente={gerente}
            />
          )}
        <Message
          nativeID="processar-assinatura-digital-cartao-ponto-erro"
          type="warning"
          visible={erros !== null}
          message={erros || ''}
          onRequestClose={() => this.setState({ erros: null })}
        />
      </>
    );
  }
}

const theme = getTheme();

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 16,
    paddingVertical: 10
  },
  containerProcessando: {
    minHeight: 60,
    gap: 8,
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'row'
  },
  text: {
    fontSize: isTablet() ? 18 : 14,
    color: theme.textColor3
  }
});

export default AssinaturaDigitalCartaoPontoDetalhe;
